import React, {useEffect, useState} from 'react';
import {Col, DatePicker, Form, Input, message, Row, Select, Skeleton} from 'antd';
import {useNavigate} from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {useTranslation} from '../../../../hooks/useTranslation';
import {useDateFormat} from '../../../../hooks/useDateFormat';
import {useEventDetail, useCreateEvent, useUpdateEvent} from '../../../../hooks/events/useEvents';
import CurrencyInput from '../../../../components/number/CurrencyInput';

dayjs.extend(customParseFormat);

const {TextArea} = Input;

export interface EventFormActions {
  submit: () => void;
}

interface EventFormProps {
  eventId?: string;
  onRegisterActions?: (actions: EventFormActions) => void;
  onSavingChange?: (saving: boolean) => void;
}

const EventForm: React.FC<EventFormProps> = ({eventId, onRegisterActions, onSavingChange}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const {t} = useTranslation();
  const {dateFormat} = useDateFormat();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const isEditMode = !!eventId && eventId !== 'new';

  const {data: eventData, isLoading} = useEventDetail(isEditMode ? eventId : undefined);
  const updateEventMutation = useUpdateEvent();
  const createEventMutation = useCreateEvent();

  useEffect(() => {
    if (eventData && isEditMode) {
      form.setFieldsValue({
        shortDescription: eventData.shortDescription,
        detailedDescription: (eventData as any).detailedDescription,
        occurrenceDate: eventData.occurrenceDate ? dayjs(eventData.occurrenceDate) : null,
        discoveryDate: eventData.discoveryDate ? dayjs(eventData.discoveryDate) : null,
        accountingDate: (eventData as any).accountingDate ? dayjs((eventData as any).accountingDate) : null,
        severityTier: eventData.severityTier,
        lossEventType: (eventData as any).lossEventType,
        businessLine: (eventData as any).businessLine,
        grossLoss: eventData.grossLoss,
        netLoss: eventData.netLoss,
        status: eventData.status?.code,
      });
    }
  }, [eventData, isEditMode, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      onSavingChange?.(true);

      const payload = {
        ...values,
        occurrenceDate: values.occurrenceDate?.format('YYYY-MM-DD'),
        discoveryDate: values.discoveryDate?.format('YYYY-MM-DD'),
        accountingDate: values.accountingDate?.format('YYYY-MM-DD'),
      };

      if (isEditMode) {
        await updateEventMutation.mutateAsync({id: eventId!, data: payload});
        messageApi.success(t('events.form.saveSuccess'));
      } else {
        const result = await createEventMutation.mutateAsync(payload);
        messageApi.success(t('events.form.createSuccess'));
        navigate(`/events/${(result as any)?.id || ''}`, {replace: true});
      }
    } catch {
      messageApi.error(t('events.form.saveError'));
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  useEffect(() => {
    onRegisterActions?.({submit: handleSave});
  }, [onRegisterActions]);

  if (isLoading) return <Skeleton active style={{padding: 24}}/>;

  return (
    <div style={{padding: 24}}>
      {contextHolder}
      <Form form={form} layout="vertical">
        <Row gutter={24}>
          {/* Left column */}
          <Col xs={24} md={12}>
            <Form.Item
              name="shortDescription"
              label={t('events.form.shortDescription')}
              rules={[{required: true, message: t('events.form.shortDescriptionRequired')}]}
            >
              <Input placeholder={t('events.form.shortDescriptionPlaceholder')}/>
            </Form.Item>

            <Form.Item name="detailedDescription" label={t('events.form.detailedDescription')}>
              <TextArea rows={4} placeholder={t('events.form.detailedDescriptionPlaceholder')}/>
            </Form.Item>

            <Form.Item name="occurrenceDate" label={t('events.form.occurrenceDate')}>
              <DatePicker style={{width: '100%'}} format={dateFormat}/>
            </Form.Item>

            <Form.Item name="discoveryDate" label={t('events.form.discoveryDate')}>
              <DatePicker style={{width: '100%'}} format={dateFormat}/>
            </Form.Item>

            <Form.Item name="accountingDate" label={t('events.form.accountingDate')}>
              <DatePicker style={{width: '100%'}} format={dateFormat}/>
            </Form.Item>
          </Col>

          {/* Right column */}
          <Col xs={24} md={12}>
            <Form.Item name="severityTier" label={t('events.form.severityTier')}>
              <Select
                placeholder={t('events.form.severityTierPlaceholder')}
                options={[
                  {value: 'TIER_1', label: 'TIER 1'},
                  {value: 'TIER_2', label: 'TIER 2'},
                  {value: 'TIER_3', label: 'TIER 3'},
                  {value: 'TIER_4', label: 'TIER 4'},
                ]}
              />
            </Form.Item>

            <Form.Item name="lossEventType" label={t('events.form.lossEventType')}>
              <Select placeholder={t('events.form.lossEventTypePlaceholder')} options={[]}/>
            </Form.Item>

            <Form.Item name="businessLine" label={t('events.form.businessLine')}>
              <Select placeholder={t('events.form.businessLinePlaceholder')} options={[]}/>
            </Form.Item>

            <Form.Item name="grossLoss" label={t('events.form.grossLoss')}>
              <CurrencyInput/>
            </Form.Item>

            <Form.Item name="netLoss" label={t('events.form.netLoss')}>
              <CurrencyInput/>
            </Form.Item>

            <Form.Item name="status" label={t('events.form.status')}>
              <Select placeholder={t('events.form.statusPlaceholder')} options={[]}/>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default EventForm;
